import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Download, Edit, Trash, Eye, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DocumentListItem } from "./document-list/DocumentListItem";

interface Document {
  id: string;
  name: string;
  size: number;
  type: string;
  path: string;
  created_at: string;
}

interface DocumentListProps {
  documents: Document[];
  onDocumentUpdate: () => void;
}

export const DocumentList = ({ documents, onDocumentUpdate }: DocumentListProps) => {
  const { toast } = useToast();
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [newName, setNewName] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleDownload = async (path: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(path);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      link.remove();

      toast({
        title: "Success",
        description: "File downloaded successfully!",
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Error",
        description: "Failed to download file. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string, path: string) => {
    try {
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([path]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .match({ id });

      if (dbError) throw dbError;

      onDocumentUpdate();
      toast({
        title: "Success",
        description: "Document deleted successfully!",
      });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Error",
        description: "Failed to delete document. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleEdit = async () => {
    if (!editingDoc || !newName) return;

    try {
      const { error } = await supabase
        .from('documents')
        .update({ name: newName })
        .match({ id: editingDoc.id });

      if (error) throw error;

      onDocumentUpdate();
      setEditingDoc(null);
      setNewName("");
      toast({
        title: "Success",
        description: "Document name updated successfully!",
      });
    } catch (error) {
      console.error('Edit error:', error);
      toast({
        title: "Error",
        description: "Failed to update document name. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePreview = async (path: string) => {
    try {
      const { data } = await supabase.storage
        .from('documents')
        .getPublicUrl(path);

      setPreviewUrl(data.publicUrl);
    } catch (error) {
      console.error('Preview error:', error);
      toast({
        title: "Error",
        description: "Failed to preview document. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="glass-card p-6 rounded-xl shadow-lg">
      <h2 className="text-xl font-semibold mb-4 text-foreground">Your Documents</h2>
      <div className="space-y-2">
        {documents.map((doc) => (
          <DocumentListItem
            key={doc.id}
            document={doc}
            onEdit={() => {
              setEditingDoc(doc);
              setNewName(doc.name);
            }}
            onPreview={() => handlePreview(doc.path)}
            onDownload={() => handleDownload(doc.path, doc.name)}
            onDelete={() => handleDelete(doc.id, doc.path)}
          />
        ))}

        <Dialog>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Document Name</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter new name"
              />
              <Button onClick={handleEdit} className="w-full">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Document Preview</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {previewUrl && (
                <iframe
                  src={previewUrl}
                  className="w-full h-[600px]"
                  title="Document Preview"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
